import { Dialog } from '@ori-ui/components';
import { InfoCircle } from '@ori-ui/icons';
import { Button, styled, Typography } from '@ori-ui/mui';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

const StyledInfoCircle = styled(InfoCircle)(({ theme }) => ({
  color: theme.palette.info.main,
  fontSize: '90px !important',
}));

const ConfirmationTypography = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const QuestionTypography = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(3),
}));

const Buttons = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(6),
  width: '100%',
}));

const ConfirmButton = styled(Button)({
  width: '100%',
});

const CancelButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(1),
  width: '100%',
  textDecoration: 'underline',
}));

export interface ConfirmationDialogProps {
  onConfirm: () => void;
  onCancel: () => void;
}

export const ConfirmationDialog = (props: ConfirmationDialogProps) => {
  const { onCancel, onConfirm } = props;

  const [isOpened, setOpened] = useState<boolean>(true);
  const [areButtonsActive, setButtonsActive] = useState<boolean>(true);

  const confirm = useCallback(() => {
    setButtonsActive(false);
    onConfirm();
  }, [onConfirm]);

  const cancel = useCallback(() => {
    setButtonsActive(false);
    setOpened(false);
    onCancel();
  }, [onCancel]);

  return (
    <Dialog
      hideCloseButton
      open={isOpened}
      sx={{
        textAlign: 'center',
        '& .MuiOriDialog-content': {
          maxWidth: '380px',
        },
      }}
      onClose={cancel}
    >
      <StyledInfoCircle fontSize="large" />

      <ConfirmationTypography variant="h4">
        <FormattedMessage
          id="DialogTitle"
          defaultMessage="Confirmation"
        />
      </ConfirmationTypography>

      <QuestionTypography>
        <FormattedMessage
          id="DialogQuestion"
          defaultMessage="Are you sure you want to log out?"
        />
      </QuestionTypography>

      <Buttons>
        <ConfirmButton
          variant="contained"
          color="primary"
          disabled={!areButtonsActive}
          onClick={confirm}
        >
          <FormattedMessage
            id="ConfirmationLabel"
            defaultMessage="Confirm"
          />
        </ConfirmButton>

        <CancelButton
          variant="text"
          disabled={!areButtonsActive}
          onClick={cancel}
        >
          <FormattedMessage
            id="CancelLabel"
            defaultMessage="Cancel"
          />
        </CancelButton>
      </Buttons>
    </Dialog>
  );
};
